import { coerceEnumByLabel } from '../../../lib/api/coerceEnum';
import type {
  ClosingStatus,
  LeadPipelineStageCategory,
  LeadPipelineStageDetail,
  LeadPipelineStageListItem,
} from './types';

const STAGE_CATEGORY_LABELS: Record<string, LeadPipelineStageCategory> = {
  Open: 1,
  Closed: 2,
};

const CLOSING_STATUS_LABELS: Record<string, Exclude<ClosingStatus, 0>> = {
  Won: 2,
  Lost: 3,
  Disqualified: 4,
};

function asBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  return Boolean(value);
}

export function normalizeStageCategory(value: unknown): LeadPipelineStageCategory {
  return coerceEnumByLabel(value, STAGE_CATEGORY_LABELS) ?? 1;
}

export function normalizeClosingStatus(value: unknown): ClosingStatus | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (value === 0 || value === 'None') {
    return null;
  }
  const normalized = coerceEnumByLabel(value, CLOSING_STATUS_LABELS);
  return normalized ?? null;
}

export function isClosedStageCategory(value: unknown): boolean {
  return normalizeStageCategory(value) === 2;
}

export function stageCategoryLabel(value: unknown): string {
  return isClosedStageCategory(value) ? 'Closed' : 'Open';
}

export function closingOutcomeLabel(value: unknown): string {
  const normalized = normalizeClosingStatus(value);
  if (normalized === null) {
    return '—';
  }
  switch (normalized) {
    case 2:
      return 'Won';
    case 3:
      return 'Lost';
    case 4:
      return 'Disqualified';
    default:
      return '—';
  }
}

export function normalizeLeadPipelineStageListItem(
  item: LeadPipelineStageListItem,
): LeadPipelineStageListItem {
  return {
    ...item,
    sortOrder: Number(item.sortOrder) || 0,
    category: normalizeStageCategory(item.category),
    closingStatus: normalizeClosingStatus(item.closingStatus),
    isActive: asBoolean(item.isActive),
    isSystem: asBoolean(item.isSystem),
  };
}

export function normalizeLeadPipelineStageDetail(
  item: LeadPipelineStageDetail,
): LeadPipelineStageDetail {
  const raw = item as unknown as Record<string, unknown>;
  const classificationLocked = asBoolean(raw.classificationLocked);
  return {
    ...item,
    sortOrder: Number(item.sortOrder) || 0,
    category: normalizeStageCategory(item.category),
    closingStatus: normalizeClosingStatus(item.closingStatus),
    isActive: asBoolean(item.isActive),
    isSystem: asBoolean(item.isSystem),
    canChangeClassification: !classificationLocked,
  };
}

export function sortLeadPipelineStages<T extends { sortOrder: number; label: string }>(
  stages: T[],
): T[] {
  return [...stages].sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) {
      return a.sortOrder - b.sortOrder;
    }
    return a.label.localeCompare(b.label);
  });
}
